import type { UseFormReturn } from 'react-hook-form';
import { useEffect, useRef } from 'react';
import { useMergeRefs } from '@floating-ui/react';
import {
  getTextContrastColor,
  isValidHexColor,
  useElementSize,
} from '@sixui/ui';
import { shapeTokens } from '@sixui/ui/themes/base/shape.stylex';
import stylex from '@stylexjs/stylex';
import Konva from 'konva';
import { Image, Layer, Stage } from 'react-konva';

import type { IContainerProps } from '~/components/utils/Container';
import type { ISize } from '~/helpers/types';
import type { IImageCreatorFormValues } from './ImageCreatorForm';

export type IImageCreatorFormCanvasProps = IContainerProps & {
  form: UseFormReturn<IImageCreatorFormValues>;
  stageSize: ISize;
  stageRef?: React.RefObject<Konva.Stage>;
  defaultBackgroundColor?: string;
};

const ICON_HEIGHT_SCALE = 0.6;

const styles = stylex.create({
  host: {
    height: 300,
    width: '100%',
    borderRadius: shapeTokens.corner$md,
    overflow: 'hidden',
  },
});

const changeSvgColor = (svgElement: Element, color: string): void => {
  // Here we assume that the SVG elements we want to color are <path> elements.
  const paths = svgElement.querySelectorAll('path');
  paths.forEach((path) => {
    path.setAttribute('fill', color);
  });
};

export const ImageCreatorFormCanvas: React.FC<IImageCreatorFormCanvasProps> = (
  props,
) => {
  const {
    sx,
    form,
    stageSize,
    stageRef: stageRefProp,
    defaultBackgroundColor,
  } = props;

  const stageRef = useRef<Konva.Stage>(null);
  const stageHandleRef = useMergeRefs([stageRef, stageRefProp]);

  const backgroundLayerRef = useRef<Konva.Layer>(null);
  const iconLayerRef = useRef<Konva.Layer>(null);
  const iconImageRef = useRef<Konva.Image>(null);

  const backgroundColor = form.watch('backgroundColor');
  const backgroundGradientColor = form.watch('backgroundGradientColor');
  const foregroundColor = form.watch('foregroundColor');
  const icon = form.watch('icon');

  useEffect(() => {
    const validBackgroundColor =
      backgroundColor && isValidHexColor(backgroundColor)
        ? backgroundColor
        : defaultBackgroundColor || '#ffffff';

    const validBackgroundGradientColor =
      backgroundGradientColor && isValidHexColor(backgroundGradientColor)
        ? backgroundGradientColor
        : undefined;

    const backgroundLayer = backgroundLayerRef.current;
    const stage = stageRef.current;
    if (!backgroundLayer || !stage) {
      return;
    }

    backgroundLayer.clear();

    const backgroundRect = new Konva.Rect({
      x: 0,
      y: 0,
      width: stage.width(),
      height: stage.height(),
      fillLinearGradientStartPoint: { x: 0, y: stage.height() },
      fillLinearGradientEndPoint: { x: stage.width(), y: 0 },
      fillLinearGradientColorStops: [
        0,
        validBackgroundColor,
        0.75,
        validBackgroundGradientColor || validBackgroundColor,
      ],
      listening: false,
    });

    backgroundLayer.add(backgroundRect);
    backgroundLayer.draw();
  }, [backgroundColor, backgroundGradientColor, defaultBackgroundColor]);

  useEffect(() => {
    if (!icon || !backgroundColor) {
      return;
    }

    const stage = stageRef.current;
    const image = iconImageRef.current;
    if (!stage || !image) {
      return;
    }

    const validForegroundColor =
      foregroundColor && isValidHexColor(foregroundColor)
        ? foregroundColor
        : getTextContrastColor(backgroundColor);

    const parser = new DOMParser();
    const svgDocument = parser.parseFromString(icon, 'image/svg+xml');
    const svgElement = svgDocument.documentElement;
    const svgViewBox = svgElement.getAttribute('viewBox') ?? '';
    const [svgWidth, svgHeight] = svgViewBox.split(' ').slice(2).map(Number);
    // Looks like Firefox (at least some versions of Firefox) can't draw svgs
    // with no width and height, so we always set them.
    svgElement.setAttribute('width', String(svgWidth));
    svgElement.setAttribute('height', String(svgHeight));
    const svgRatio = (svgWidth ?? 0) / (svgHeight ?? 1);
    changeSvgColor(svgElement, validForegroundColor);

    const svgData = new XMLSerializer().serializeToString(svgElement);
    const svgImage = new window.Image();
    const svgBlob = new Blob([svgData], {
      type: 'image/svg+xml;charset=utf-8',
    });
    const url = URL.createObjectURL(svgBlob);

    svgImage.onload = () => {
      URL.revokeObjectURL(url);
      image.image(svgImage);
      const iconHeight = stage.height() * ICON_HEIGHT_SCALE;
      const iconWidth = iconHeight * svgRatio;
      image.x((stage.width() - iconWidth) / 2);
      image.y((stage.height() - iconHeight) / 2);
      image.width(iconWidth);
      image.height(iconHeight);
    };
    svgImage.src = url;
  }, [icon, backgroundColor, foregroundColor]);

  const hostRef = useRef<HTMLDivElement>(null);
  const hostSize = useElementSize({
    ref: hostRef,
    observe: true,
  });

  useEffect(() => {
    const stage = stageRef.current;
    if (!stage || !hostSize) {
      return;
    }

    stage.width(hostSize.width);
    stage.height(hostSize.height);
    stage.draw();
  }, [hostSize]);

  return (
    <div {...stylex.props(styles.host, sx)} ref={hostRef}>
      <Stage
        width={stageSize.width}
        height={stageSize.height}
        backgroundColor={'red'}
        ref={stageHandleRef}
      >
        <Layer ref={backgroundLayerRef} />
        <Layer ref={iconLayerRef}>
          <Image image={undefined} ref={iconImageRef} />
        </Layer>
      </Stage>
    </div>
  );
};
